import { useLocalSearchParams } from 'expo-router'
import { Bug, Lock, Mail, MapPin, Pencil, Trash2, User } from 'lucide-react-native'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, type ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native'
import {
    Dialog,
    Divider,
    Button as PaperButton,
    Portal,
    Switch,
    Text,
    useTheme,
} from 'react-native-paper'
import { TrackingPauseSection } from '@/components/TrackingPauseSection'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useDeviceTrackingSettings } from '@/hooks/useDeviceTrackingSettings'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useChangePassword, useDeleteAccount, useUpdateSettings } from '@/hooks/useMyDetails'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { useSendDebugLogs } from '@/hooks/useSendDebugLogs'
import { useAuth } from '@/lib/auth'
import { BREAKPOINTS } from '@/lib/config'
import { log } from '@/lib/logger'
import {
    changePasswordSchema,
    getDefaultFormValues,
    myDetailsSchema,
    useSetShowDeleteDialog,
    useSetShowPasswordDialog,
    useShowDeleteDialog,
    useShowPasswordDialog,
} from '@/stores/my-details-store'
import { Card } from '@/ui/Card'
import { TextInput, useForm, zodResolver } from '@/ui/form'
import { LocationPermissionWarning } from '@/ui/LocationPermissionWarning'
import { Page } from '@/ui/Page'

export default function MyDetailsPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{
        orgSlug: string
        scrollTo?: string
    }>()
    const { user } = useAuth()
    const theme = useTheme()
    const { width } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg

    const scrollViewRef = useRef<ScrollView>(null)
    const locationSectionY = useRef<number>(0)

    const {
        userOrgId,
        workingHoursOverride,
        workStartTime,
        workEndTime,
        workingDays,
        trackingPausedUntil,
    } = useOrgBySlug(params.orgSlug)

    const userOrgData = useMemo(
        () => ({
            workingHoursOverride,
            workStartTime,
            workEndTime,
            workingDays,
            trackingPausedUntil,
        }),
        [workingHoursOverride, workStartTime, workEndTime, workingDays, trackingPausedUntil]
    )

    const showDeleteDialog = useShowDeleteDialog()
    const setShowDeleteDialog = useSetShowDeleteDialog()
    const dismissDeleteDialog = useCallback(() => setShowDeleteDialog(false), [setShowDeleteDialog])
    useDismissOnEscape(showDeleteDialog, dismissDeleteDialog)

    const showPasswordDialog = useShowPasswordDialog()
    const setShowPasswordDialog = useSetShowPasswordDialog()
    const dismissPasswordDialog = useCallback(
        () => setShowPasswordDialog(false),
        [setShowPasswordDialog]
    )
    useDismissOnEscape(showPasswordDialog, dismissPasswordDialog)

    const updateSettings = useUpdateSettings()
    const deleteAccount = useDeleteAccount()
    const changePassword = useChangePassword()
    const debugLogs = useSendDebugLogs()

    const passwordForm = useForm({
        resolver: zodResolver(changePasswordSchema),
        defaultValues: { oldPassword: '', password: '', passwordConfirm: '' },
    })

    const handleChangePassword = passwordForm.handleSubmit(data => {
        changePassword.mutate(data, {
            onSuccess: () => {
                setShowPasswordDialog(false)
                passwordForm.reset()
            },
        })
    })

    const {
        deviceTrackingDisabled,
        setDeviceTrackingDisabled,
        showPermissionWarning,
        isLoading: deviceSettingsLoading,
    } = useDeviceTrackingSettings()

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { isDirty },
    } = useForm({
        resolver: zodResolver(myDetailsSchema),
        defaultValues: getDefaultFormValues(user, userOrgData),
        mode: 'onChange',
    })

    const formValues = watch()

    useEffect(() => {
        reset(getDefaultFormValues(user, userOrgData))
    }, [user, userOrgData, reset])

    useEffect(() => {
        if (params.scrollTo === 'location' && locationSectionY.current > 0) {
            setTimeout(() => {
                scrollViewRef.current?.scrollTo({
                    y: locationSectionY.current,
                    animated: true,
                })
            }, 100)
        }
    }, [params.scrollTo])

    useHeaderOptions({
        buttons: {
            type: 'save',
            onPress: handleSubmit(data =>
                updateSettings.mutate({
                    data,
                    orgSlug: params.orgSlug,
                    userOrgId,
                    userOrgData,
                })
            ),
            loading: updateSettings.isPending,
            loadingTitle: 'Saving...',
        },
    })

    const [editingProfile, setEditingProfile] = useState(false)

    const handleDeleteAccount = () => {
        if (!userOrgId) return
        deleteAccount.mutate(
            { userOrgId },
            {
                onSettled: () => setShowDeleteDialog(false),
            }
        )
    }

    log.debug('my-details trackingPausedUntil:', {
        fromHook: trackingPausedUntil,
        fromForm: formValues.tracking_paused_until,
        userOrgData,
    })

    return (
        <Page scrollViewRef={scrollViewRef}>
            <Card>
                <Card.Content style={styles.cardContent}>
                    <Text variant="titleLarge" style={styles.sectionTitle}>
                        Account Settings
                    </Text>

                    <View style={styles.section}>
                        <View style={styles.profileHeader}>
                            <Text variant="titleMedium" style={styles.subsectionTitle}>
                                Profile Information
                            </Text>
                            <PaperButton
                                mode="text"
                                compact
                                icon={({ size, color }) => <Pencil size={size - 4} color={color} />}
                                onPress={() => setEditingProfile(prev => !prev)}
                            >
                                {editingProfile ? 'Done' : 'Edit'}
                            </PaperButton>
                        </View>

                        {editingProfile ? (
                            <View style={[styles.fieldRow, isDesktop && styles.fieldRowDesktop]}>
                                <View style={[styles.fieldWrapper, isDesktop && styles.fieldHalf]}>
                                    <View style={styles.fieldHeader}>
                                        <User size={16} color={theme.colors.onSurfaceVariant} />
                                        <Text
                                            variant="labelLarge"
                                            style={[
                                                styles.fieldLabel,
                                                { color: theme.colors.onSurface },
                                            ]}
                                        >
                                            Display Name
                                        </Text>
                                    </View>
                                    <TextInput
                                        name="name"
                                        control={control}
                                        placeholder="Enter your display name"
                                    />
                                </View>

                                <View style={[styles.fieldWrapper, isDesktop && styles.fieldHalf]}>
                                    <View style={styles.fieldHeader}>
                                        <Mail size={16} color={theme.colors.onSurfaceVariant} />
                                        <Text
                                            variant="labelLarge"
                                            style={[
                                                styles.fieldLabel,
                                                { color: theme.colors.onSurface },
                                            ]}
                                        >
                                            Email Address
                                        </Text>
                                    </View>
                                    <TextInput
                                        name="email"
                                        control={control}
                                        placeholder="Enter your email address"
                                        keyboardType="email-address"
                                        autoCapitalize="none"
                                        autoCorrect={false}
                                    />
                                </View>
                            </View>
                        ) : (
                            <View style={styles.profileDisplay}>
                                <View style={styles.profileRow}>
                                    <User size={16} color={theme.colors.onSurfaceVariant} />
                                    <Text variant="bodyLarge">
                                        {formValues.name || 'No name set'}
                                    </Text>
                                </View>
                                <View style={styles.profileRow}>
                                    <Mail size={16} color={theme.colors.onSurfaceVariant} />
                                    <Text variant="bodyLarge">
                                        {formValues.email || 'No email set'}
                                    </Text>
                                </View>
                            </View>
                        )}
                    </View>

                    <Divider style={styles.sectionDivider} />

                    <View
                        style={styles.section}
                        onLayout={e => {
                            locationSectionY.current = e.nativeEvent.layout.y
                        }}
                    >
                        <View style={styles.geoHeader}>
                            <MapPin size={18} color={theme.colors.onSurfaceVariant} />
                            <Text variant="titleMedium" style={styles.subsectionTitle}>
                                Location Tracking
                            </Text>
                        </View>
                        <Text
                            variant="bodyMedium"
                            style={[styles.geoHint, { color: theme.colors.onSurfaceVariant }]}
                        >
                            Control whether your location is tracked for automatic time entries.
                        </Text>

                        <View style={styles.toggleRow}>
                            <Text variant="bodyMedium">Enable for my account</Text>
                            <Switch
                                value={!formValues.geo_tracking_disabled}
                                onValueChange={value => {
                                    setValue('geo_tracking_disabled', !value, {
                                        shouldDirty: true,
                                    })
                                }}
                                disabled={updateSettings.isPending}
                            />
                        </View>

                        {!formValues.geo_tracking_disabled && (
                            <>
                                {Platform.OS !== 'web' && (
                                    <View style={styles.subPanel}>
                                        <View style={styles.toggleRow}>
                                            <View style={styles.toggleLabel}>
                                                <Text variant="bodyMedium">
                                                    Use the location of this
                                                </Text>
                                                <Text variant="bodyMedium">
                                                    device to track your time
                                                </Text>
                                            </View>
                                            <Switch
                                                value={!deviceTrackingDisabled}
                                                onValueChange={value =>
                                                    setDeviceTrackingDisabled(!value)
                                                }
                                                disabled={deviceSettingsLoading}
                                            />
                                        </View>

                                        <LocationPermissionWarning
                                            isVisible={showPermissionWarning}
                                            onEnableDeviceFlag={() =>
                                                setDeviceTrackingDisabled(true)
                                            }
                                        />
                                    </View>
                                )}

                                <Divider style={styles.internalDivider} />
                                <TrackingPauseSection
                                    pausedUntil={formValues.tracking_paused_until ?? null}
                                    onSetPause={date =>
                                        setValue('tracking_paused_until', date, {
                                            shouldDirty: true,
                                        })
                                    }
                                    disabled={updateSettings.isPending}
                                />
                            </>
                        )}
                    </View>

                    <Divider style={styles.sectionDivider} />

                    <View style={styles.section}>
                        <View style={styles.passwordHeader}>
                            <Lock size={18} color={theme.colors.onSurfaceVariant} />
                            <Text variant="titleMedium" style={styles.subsectionTitle}>
                                Password
                            </Text>
                        </View>
                        <PaperButton
                            mode="outlined"
                            onPress={() => setShowPasswordDialog(true)}
                            icon="lock-reset"
                            style={styles.changePasswordButton}
                        >
                            Change Password
                        </PaperButton>
                    </View>

                    {Platform.OS !== 'web' && (
                        <>
                            <Divider style={styles.sectionDivider} />

                            <View style={styles.section}>
                                <View style={styles.debugHeader}>
                                    <Bug size={18} color={theme.colors.onSurfaceVariant} />
                                    <Text variant="titleMedium" style={styles.subsectionTitle}>
                                        Troubleshooting
                                    </Text>
                                </View>
                                <Text
                                    variant="bodyMedium"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    Send location tracking logs to help diagnose issues with
                                    automatic time entries.
                                </Text>
                                <PaperButton
                                    mode="outlined"
                                    onPress={() => debugLogs.sendLogs()}
                                    loading={debugLogs.isPending}
                                    icon={({ size, color }) => (
                                        <Bug size={size - 4} color={color} />
                                    )}
                                    style={styles.sendLogsButton}
                                >
                                    {debugLogs.isPending ? 'Sending...' : 'Send Logs to Developer'}
                                </PaperButton>
                            </View>
                        </>
                    )}

                    <Divider style={styles.sectionDivider} />

                    <View style={styles.section}>
                        <View style={styles.deleteHeader}>
                            <Trash2 size={18} color={theme.colors.error} />
                            <Text
                                variant="titleMedium"
                                style={[styles.subsectionTitle, { color: theme.colors.error }]}
                            >
                                Delete Account
                            </Text>
                        </View>
                        <Text
                            variant="bodyMedium"
                            style={[styles.deleteHint, { color: theme.colors.onSurfaceVariant }]}
                        >
                            Deactivate your account and lose access. Your organization may still
                            retain records for auditing purposes.
                        </Text>
                        <PaperButton
                            mode="outlined"
                            onPress={() => setShowDeleteDialog(true)}
                            textColor={theme.colors.error}
                            style={[styles.deleteButton, { borderColor: theme.colors.error }]}
                        >
                            Delete My Account
                        </PaperButton>
                    </View>
                </Card.Content>
            </Card>

            <Portal>
                <Dialog
                    visible={showPasswordDialog}
                    onDismiss={dismissPasswordDialog}
                    style={styles.passwordDialog}
                >
                    <Dialog.Title>Change Password</Dialog.Title>
                    <Dialog.Content style={styles.passwordDialogContent}>
                        <TextInput
                            name="oldPassword"
                            control={passwordForm.control}
                            label="Current Password"
                            placeholder="Enter your current password"
                            secureTextEntry
                            autoComplete="current-password"
                        />
                        <TextInput
                            name="password"
                            control={passwordForm.control}
                            label="New Password"
                            placeholder="Enter your new password"
                            secureTextEntry
                            autoComplete="new-password"
                        />
                        <TextInput
                            name="passwordConfirm"
                            control={passwordForm.control}
                            label="Confirm New Password"
                            placeholder="Re-enter your new password"
                            secureTextEntry
                            autoComplete="new-password"
                        />
                    </Dialog.Content>
                    <Dialog.Actions>
                        <PaperButton onPress={dismissPasswordDialog}>Cancel</PaperButton>
                        <PaperButton
                            onPress={handleChangePassword}
                            loading={changePassword.isPending}
                        >
                            {changePassword.isPending ? 'Changing...' : 'Change Password'}
                        </PaperButton>
                    </Dialog.Actions>
                </Dialog>

                <Dialog visible={showDeleteDialog} onDismiss={() => setShowDeleteDialog(false)}>
                    <Dialog.Title>Delete Account?</Dialog.Title>
                    <Dialog.Content>
                        <Text variant="bodyMedium">
                            Are you sure you want to deactivate your account? This will:
                        </Text>
                        <View style={styles.deleteWarningList}>
                            <Text variant="bodyMedium">
                                {'\u2022'} Disable your login and remove access
                            </Text>
                            <Text variant="bodyMedium">
                                {'\u2022'} Remove you from all organizations
                            </Text>
                        </View>
                        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                            Your organization may still retain records for auditing purposes.
                        </Text>
                    </Dialog.Content>
                    <Dialog.Actions>
                        <PaperButton onPress={() => setShowDeleteDialog(false)}>Cancel</PaperButton>
                        <PaperButton
                            onPress={handleDeleteAccount}
                            loading={deleteAccount.isPending}
                            textColor={theme.colors.error}
                        >
                            {deleteAccount.isPending ? 'Deleting...' : 'Delete Account'}
                        </PaperButton>
                    </Dialog.Actions>
                </Dialog>
            </Portal>
            <UnsavedChangesDialog isDirty={isDirty} />
        </Page>
    )
}

const styles = StyleSheet.create({
    cardContent: {
        gap: 16,
    },
    sectionTitle: {
        fontWeight: '600',
    },
    section: {
        gap: 12,
    },
    profileHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    profileDisplay: {
        gap: 8,
    },
    profileRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    subsectionTitle: {
        fontWeight: '600',
        marginBottom: 4,
    },
    fieldRow: {
        gap: 12,
    },
    fieldRowDesktop: {
        flexDirection: 'row',
        gap: 16,
    },
    fieldWrapper: {
        gap: 8,
    },
    fieldHalf: {
        flex: 1,
    },
    fieldHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    fieldLabel: {
        fontWeight: '600',
    },
    sectionDivider: {
        marginVertical: 8,
    },
    passwordHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    changePasswordButton: {
        alignSelf: 'flex-start',
    },
    passwordDialog: {
        maxWidth: 500,
        alignSelf: 'center',
        width: '100%',
    },
    passwordDialogContent: {
        gap: 12,
    },
    geoHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    geoHint: {
        lineHeight: 20,
    },
    subPanel: {
        marginTop: 8,
        paddingLeft: 12,
        gap: 12,
    },
    toggleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    toggleLabel: {
        flex: 1,
        gap: 2,
    },
    internalDivider: {
        marginVertical: 8,
    },
    debugHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    sendLogsButton: {
        alignSelf: 'flex-start',
    },
    deleteHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    deleteHint: {
        lineHeight: 20,
    },
    deleteButton: {
        marginTop: 8,
        alignSelf: 'flex-start',
    },
    deleteWarningList: {
        marginTop: 12,
        marginBottom: 12,
        gap: 4,
    },
    deleteWarning: {
        fontWeight: '600',
    },
})
